<template>
    <div class="header">
        <div class="logo logo-container">
            <router-link to="/">
                <img src="/logo.png" alt="浙江国正安全技术有限公司logo" />
            </router-link>

            <!-- 移动端菜单按钮 -->
            <div class="menu-toggle" @click="toggleMobileMenu">
                <img src="@/assets/images/icon/cdl.png" alt="menu" />
            </div>

            <!-- 导航 -->
            <div class="nav" :class="{ 'nav-open': isMobileMenuOpen }">
                <template v-for="menu in menuList" :key="menu.id">
                    <!-- 有子菜单 -->
                    <router-link v-if="menu.children && menu.children.length" :class="[{ active: isMenuActive(menu) }]"
                        :to="menuPath(menu)" @click="toggleSubMenu(menu.id, $event)">
                        {{ menu.name }}
                        <span class="mobile-arrow" v-if="isMobile">
                            {{ subMenuStates[menu.id] ? '▼' : '▶' }}
                        </span>

                        <!-- 二级菜单 -->
                        <div class="a-wrap" :class="[
                            getWrapClass(menu),
                            { 'mobile-show': subMenuStates[menu.id] }
                        ]">
                            <div class="subnav">
                                <ul class="flex">
                                    <li v-for="sub in menu.children" :key="sub.id">
                                        <router-link
                                            :to="subPath(sub)"
                                            @click="closeMobileMenu"
                                        >
                                            {{ sub.name }}
                                        </router-link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </router-link>

                    <!-- 无子菜单 -->
                    <router-link v-else :class="[{ active: isMenuActive(menu) }]" :to="menuPath(menu)" @click="closeMobileMenu">
                        {{ menu.name }}
                    </router-link>
                </template>

                <!-- 搜索按钮 -->
                <div class="search-btn" @click="toggleSearchBox">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                </div>
            </div>

            <!-- 全局搜索框 -->
            <div class="search-overlay" v-if="showSearchBox" @click="closeSearchBox">
                <div class="search-container" @click.stop>
                    <div class="search-box">
                        <input type="text" v-model="searchKeyword" placeholder="请输入搜索关键词..."
                            @keyup.enter="performSearch" ref="searchInput" />
                        <button @click="performSearch" class="search-submit">搜索</button>
                    </div>
                    <button class="close-btn" @click="closeSearchBox">×</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { getWebsiteType } from '@/utils/api'
import { useRouter } from 'vue-router'
const router = useRouter()

const currentHref = ref(window.location.pathname + window.location.search)

const isMobileMenuOpen = ref(false)
const isMobile = ref(false)
const subMenuStates = ref({}) // 动态生成

// 搜索相关状态
const showSearchBox = ref(false)
const searchKeyword = ref('')
const searchInput = ref(null)



// 检查是否为移动端
const checkMobile = () => {
    isMobile.value = window.innerWidth <= 768
}

const isMenuActive = (menu) => {
    const path = menuPath(menu)
    const current = currentHref.value

    // 首页特殊处理
    if (path === '/') return current === '/'

    // 带参数的路径
    if (path.includes('?')) {
        const [base] = path.split('?')
        if (current.startsWith(base)) return true
    }

    // 普通路径匹配
    if (current.startsWith(path)) return true

    // 子菜单路径匹配（关键）
    if (menu.children && menu.children.length) {
        return menu.children.some(sub => {
            const subUrl = subPath(sub)
            if (current.startsWith(subUrl)) return true

            // 处理详情页 type 参数匹配
            const params = new URLSearchParams(current.split('?')[1])
            if (params.get('type') == sub.id) return true

            return false
        })
    }

    return false
}


// 菜单路径生成规则
const menuPath = (menu) => {
    switch (menu.name) {
        case '首页': return '/'
        case '关于国正': return '/about'
        case '服务项目': return '/products'
        case '新闻中心': return `/news?id=${menu.id}`
        case '网上信息公开': return `/companynews?id=${menu.children[0].id}`
        case '文件下载': return '/downloads'
        case '加入国正': return '/joinUs'
        case '联系我们': return '/contact'
        default: return `/page/${menu.id}`
    }
}

// 二级菜单路径规则
const subPath = (sub) => {
    if (sub.id === 15) return `/products/detail?id=1`
    if (sub.id === 16) return `/products/detail?id=7`
    if (sub.id === 17) return `/products/detail?id=11`
    if (sub.parentId === 2) return `/companynews?id=${sub.id}`
    if (sub.parentId === 8) {
        const map = {
            9: '/about',
            10: '/institution',
            11: '/culture',
            12: '/honors'
        }
        return map[sub.id] || `/page/${sub.id}`
    }
    return `/page/${sub.id}`
}

// 子菜单外层 class 控制
const getWrapClass = (menu) => {
    const map = {
        8: 'about-wrap',
        14: 'products-wrap',
        2: 'publicInfo-wrap'
    }
    return map[menu.id] || ''
}

// 构建树结构
const buildTree = (data) => {
    const map = {}
    const result = []

    // 先建立一个 id → item 的映射
    data.forEach(item => {
        map[item.id] = { ...item, children: [] }
    })

    // 挂载子项
    data.forEach(item => {
        if (item.parentId === 0) {
            result.push(map[item.id])
        } else {
            map[item.parentId]?.children.push(map[item.id])
        }
    })

    // 一级菜单：按 sort 升序排列（没有 sort 的排最后）
    result.sort((a, b) => {
        const sa = a.sort ?? 9999
        const sb = b.sort ?? 9999
        return sa - sb
    })

    // 二级菜单：如果存在多个子项，可以按 createTime 排序（可选）
    result.forEach(menu => {
        if (menu.children && menu.children.length > 1) {
            menu.children.sort((a, b) => {
                const ta = a.createTime ?? 0
                const tb = b.createTime ?? 0
                return ta - tb
            })
        }
    })

    return result
}


// 菜单数据
const menuList = ref([])

onMounted(async () => {
    checkMobile()
    window.addEventListener('resize', checkMobile)

    try {
        const res = await getWebsiteType()
        const tree = buildTree(res.data)
        menuList.value = tree

        // 初始化子菜单状态
        tree.forEach(item => {
            subMenuStates.value[item.id] = false
        })
    } catch (err) {
        console.error('获取菜单失败：', err)
    }
})

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile)
})

// 移动端菜单控制
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    if (!isMobileMenuOpen.value) {
        Object.keys(subMenuStates.value).forEach(k => (subMenuStates.value[k] = false))
    }
}

const closeMobileMenu = () => {
    isMobileMenuOpen.value = false
    Object.keys(subMenuStates.value).forEach(k => (subMenuStates.value[k] = false))
}

// 切换子菜单显示
const toggleSubMenu = (id, e) => {
    if (isMobile.value) {
        e.preventDefault()
        subMenuStates.value[id] = !subMenuStates.value[id]
        Object.keys(subMenuStates.value).forEach(k => {
            if (k != id) subMenuStates.value[k] = false
        })
    }
}

// 搜索功能
const toggleSearchBox = () => {
    showSearchBox.value = !showSearchBox.value
    if (showSearchBox.value) {
        nextTick(() => {
            searchInput.value?.focus()
        })
    }
}

const closeSearchBox = () => {
    showSearchBox.value = false
    searchKeyword.value = ''
}

const performSearch = () => {

    if (searchKeyword.value.trim()) {
        const keyword = encodeURIComponent(searchKeyword.value.trim())
        router.push(`/companynews/search?title=${keyword}`)
        closeSearchBox()
    }
}
</script>

<style scoped>
/* 保留你原来的 CSS 全部不动 */
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: "Source Han Sans CN", 'Noto Sans SC', "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
}

.header {
    width: 100%;
    height: 136px;
    background: #fff;
    box-shadow: 0 0 10px rgba(0, 0, 0, .1);
    position: fixed;
    left: 0;
    top: 0;
    z-index: 9999;
}

.header .logo-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 1380px;
    margin: 0 auto;
}

.logo-container .logo {
    height: 136px;
    padding: 27px 0;
}

.logo-container .nav {
    display: flex;
    height: 136px;
}

.nav a {
    text-decoration: none;
    color: #333;
    transition: color 0.3s ease;
    white-space: nowrap;
    padding: 0 20px;
    text-align: center;
    line-height: 136px;
    font-size: 18px;
}

.nav a:hover {
    color: #00437c;
}

.nav .active {
    color: #ffffff;
    background-color: #00437c;
}

.nav .active:hover {
    color: #ffffff;
}

.nav .a-wrap {
    display: none;
}

.nav a:hover .a-wrap {
    background-color: #00437c;
    height: 80px;
    width: 100%;
    position: absolute;
    left: 0;
    top: 100px;
    z-index: 999;
    display: block;
}

.nav .a-wrap .subnav {
    width: 1000px;
    position: absolute;
    left: 36%;
    top: -30px;
    z-index: 111;
}

.nav .a-wrap .subnav ul {
    display: flex;
}

.nav .a-wrap .subnav ul li {
    line-height: 74px;
    position: relative;
    z-index: 11;
}

.nav .a-wrap .subnav ul li a {
    color: #fff;
    padding: 0 20px;
    font-size: 16px;
}

.nav .a-wrap .subnav ul li a:hover {
    color: #c1a25f;
}

.nav .a-wrap .subnav ul li a::before {
    content: "■";
    color: #c1a25f;
    font-size: 12px;
    margin-right: 10px;
}

/* 特殊位置 */
.nav .publicInfo-wrap .subnav {
    left: 20%;
}

.nav .contact .a-wrap .subnav {
    left: -83%;
}

/* 搜索按钮样式 */
.search-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 136px;
    cursor: pointer;
    color: #333;
    transition: color 0.3s ease;
}

.search-btn:hover {
    color: #00437c;
}

/* 搜索框遮罩层 */
.search-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.search-container {
    background: white;
    border-radius: 8px;
    padding: 50px 30px;
    position: relative;
    min-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.search-box {
    display: flex;
    gap: 10px;
}

.search-box input {
    flex: 1;
    padding: 12px 16px;
    border: 2px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
    outline: none;
    transition: border-color 0.3s ease;
}

.search-box input:focus {
    border-color: #00437c;
}

.search-submit {
    padding: 12px 24px;
    background: #00437c;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    transition: background 0.3s ease;
}

.search-submit:hover {
    background: #003366;
}

.close-btn {
    position: absolute;
    top: 0;
    right: 5px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    color: #333;
}

/* 移动端部分保持你原来的逻辑 */
.menu-toggle {
    display: none;
    flex-direction: column;
    cursor: pointer;
    padding: 10px;
}

@media screen and (max-width: 768px) {
    .header {
        height: 60px;
    }

    .header .logo-container {
        height: 60px;
        padding: 0 15px;
    }

    .header .logo img {
        height: 40px;
        width: auto;
    }

    .menu-toggle {
        display: flex;
    }

    .nav {
        position: fixed;
        top: 60px;
        left: -100%;
        width: 100%;
        height: calc(100vh - 60px);
        background: #fff;
        flex-direction: column;
        align-items: flex-start;
        transition: left 0.3s ease;
        overflow-y: auto;
    }

    .nav.nav-open {
        left: 0;
    }

    .nav a {
        width: 100%;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
        text-align: left;
    }

    .nav .a-wrap {
        position: static;
        display: none;
    }

    .nav .a-wrap.mobile-show {
        display: block;
        background: #f8f8f8;
    }

    .search-btn {
        height: 60px;
        width: 40px;
    }

    .search-container {
        min-width: 300px;
        margin: 0 20px;
    }
}
</style>
